import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { useSoftUIController } from "hooks/context";

const PolygonCreator = ({ 
    localMap, isMapLoaded, appMode, generateTrigger, selectedColor, onPolygonGenerated, user, editGroup, groupType,
    points, selectedPointIds, idField, nameField, onPointsEnclosed, onPointToggle 
}) => {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg } = controller;

    const draw = useRef(null);
    const popupRef = useRef(null);
    const addedLayersRef = useRef([]);

    const stateRef = useRef({ points: [], selectedPointIds: [], onPointToggle, calculateEnclosedPoints: null });
    const generatedPolygonRef = useRef(null);
    const initialDrawRef = useRef(false); 

    const hasFittedBounds = useRef(false);
    const prevAppMode = useRef(appMode);
    const prevGroupType = useRef(groupType);

    useEffect(() => {
        stateRef.current.points = points;
        stateRef.current.selectedPointIds = selectedPointIds;
        stateRef.current.onPointToggle = onPointToggle;
    }, [points, selectedPointIds, onPointToggle]);

    const getCameraOptions = useCallback(() => {
        const set_padding = 150;
        return {
            padding: deviceLayout === 'mobile' ? { top: set_padding / 2, left: set_padding / 2, right: set_padding / 2, bottom: set_padding / 2 } : set_padding,
            maxZoom: 15, duration: 1200
        };
    }, [deviceLayout]);

    const calculateEnclosedPoints = useCallback(() => {
        if (!draw.current || appMode === 'review') return;
        const drawnFeatures = draw.current.getAll();
        if (drawnFeatures.features.length === 0) {
            onPointsEnclosed([]); return;
        }

        const enclosedIds = [];
        stateRef.current.points.forEach(point => {
            const pt = turf.point(point.geometry.coordinates);
            let isInside = false;
            for (const poly of drawnFeatures.features) {
                if (poly.geometry.type === 'Polygon' || poly.geometry.type === 'MultiPolygon') {
                    if (turf.booleanPointInPolygon(pt, poly)) {
                        isInside = true; break;
                    }
                }
            }
            if (isInside) enclosedIds.push(point.properties[idField]);
        });

        onPointsEnclosed(enclosedIds);
    }, [onPointsEnclosed, appMode, idField]);

    useEffect(() => { stateRef.current.calculateEnclosedPoints = calculateEnclosedPoints; }, [calculateEnclosedPoints]);

    const handlePolygonValidation = useCallback((e) => {
        if (!draw.current) return;
        if (e.features && e.features.length > 0) {
            e.features.forEach(feature => {
                if (feature.geometry.type === 'Polygon') {
                    const kinks = turf.kinks(feature);
                    if (kinks.features.length > 0) {
                        const coords = feature.geometry.coordinates[0];
                        const hull = turf.convex(turf.points(coords));
                        if (hull) { hull.id = feature.id; draw.current.add(hull); }
                    }
                }
            });
        }
        if (stateRef.current.calculateEnclosedPoints) stateRef.current.calculateEnclosedPoints();
    }, []);

    // 1. CORE SETUP
    useEffect(() => {
        if (!isMapLoaded || !localMap.current) return;
        const mapInstance = localMap.current;
        let isMounted = true;

        const setupCore = () => {
            if (!isMounted) return;

            if (!draw.current) {
                popupRef.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false });
                draw.current = new MapboxDraw({ displayControlsDefault: false, controls: { polygon: true, trash: true } });
            }

            if (!mapInstance.hasControl(draw.current)) {
                mapInstance.addControl(draw.current, 'top-right');
            }

            if (!mapInstance.getSource('final-polygon-source')) {
                mapInstance.addSource('final-polygon-source', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
                mapInstance.addLayer({ id: 'final-polygon-fill', type: 'fill', source: 'final-polygon-source', paint: { 'fill-color': '#17c1e8', 'fill-opacity': 0.2 } });
                mapInstance.addLayer({ id: 'final-polygon-outline', type: 'line', source: 'final-polygon-source', paint: { 'line-color': '#17c1e8', 'line-width': 3 } });
            }

            if (!mapInstance.getSource('points-source')) {
                mapInstance.addSource('points-source', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
            }
        };

        if (mapInstance.isStyleLoaded()) {
            setupCore();
        } else {
            mapInstance.once('style.load', setupCore);
        }

        const onDrawCreateUpdate = (e) => handlePolygonValidation(e);
        const onDrawDelete = () => { if (stateRef.current.calculateEnclosedPoints) stateRef.current.calculateEnclosedPoints(); };

        mapInstance.on('draw.create', onDrawCreateUpdate);
        mapInstance.on('draw.update', onDrawCreateUpdate);
        mapInstance.on('draw.delete', onDrawDelete);

        return () => {
            isMounted = false;
            mapInstance.off('draw.create', onDrawCreateUpdate);
            mapInstance.off('draw.update', onDrawCreateUpdate);
            mapInstance.off('draw.delete', onDrawDelete);

            if (draw.current && mapInstance.hasControl(draw.current)) mapInstance.removeControl(draw.current);
            if (popupRef.current) popupRef.current.remove();

            if (mapInstance.getStyle()) {
                ['final-polygon-fill', 'final-polygon-outline'].forEach(l => { if (mapInstance.getLayer(l)) mapInstance.removeLayer(l); });
                addedLayersRef.current.forEach(layerId => {
                    if (mapInstance.getLayer(layerId)) mapInstance.removeLayer(layerId);
                });
                addedLayersRef.current = [];
                ['final-polygon-source', 'points-source'].forEach(s => { if (mapInstance.getSource(s)) mapInstance.removeSource(s); });
            }
        };
    }, [isMapLoaded, localMap, handlePolygonValidation]);

    // TYPE SWITCH
    useEffect(() => {
        if (prevGroupType.current !== groupType) {
            if (draw.current) {
                draw.current.deleteAll();
            }
            if (localMap.current && isMapLoaded) {
                const finalSource = localMap.current.getSource('final-polygon-source');
                if (finalSource) {
                    finalSource.setData({ type: 'FeatureCollection', features: [] });
                }
            }
            hasFittedBounds.current = false; 
            prevGroupType.current = groupType;
        }
    }, [groupType, isMapLoaded, localMap]);


    // 2. POLYGON INJECTION 
    useEffect(() => {
        if (!isMapLoaded || !localMap.current || !draw.current || !editGroup || !editGroup.geojson) return;
        if (initialDrawRef.current) return; 
        if (groupType !== (editGroup.type || 'campaigns')) return; 
        
        const mapInstance = localMap.current;

        const injectPolygon = () => {
            if (initialDrawRef.current) return; 

            const addedIds = [];
            editGroup.geojson.forEach(geo => {
                const added = draw.current.add(geo);
                if (added && added.length) addedIds.push(...added);
            });
            
            initialDrawRef.current = true;
            
            if (addedIds.length > 0) {
                draw.current.changeMode('simple_select', { featureIds: addedIds });
            }

            try {
                const bbox = turf.bbox({
                    type: 'FeatureCollection',
                    features: editGroup.geojson.map(g => ({ type: 'Feature', geometry: g.geometry || g, properties: {} }))
                });
                if (bbox && bbox.every(val => isFinite(val))) {
                    mapInstance.stop();
                    setTimeout(() => {
                        if (mapInstance) {
                            mapInstance.resize();
                            mapInstance.fitBounds(new mapboxgl.LngLatBounds([bbox[0], bbox[1]], [bbox[2], bbox[3]]), getCameraOptions());
                        }
                    }, 50);
                    hasFittedBounds.current = true; 
                }
            } catch (e) {
                console.error("Failed to fit bounds to loaded polygon", e);
            }
        };

        if (mapInstance.isStyleLoaded()) {
            injectPolygon();
        } else {
            mapInstance.once('idle', injectPolygon);
        }
    }, [isMapLoaded, localMap, editGroup, getCameraOptions, groupType]);

    // 3. COLOR SYNC
    useEffect(() => {
        if (!isMapLoaded || !localMap.current) return;
        const mapInstance = localMap.current;

        const updateColors = () => {
            if (mapInstance.getLayer('final-polygon-fill')) {
                mapInstance.setPaintProperty('final-polygon-fill', 'fill-color', selectedColor || '#17c1e8');
            }
            if (mapInstance.getLayer('final-polygon-outline')) {
                mapInstance.setPaintProperty('final-polygon-outline', 'line-color', selectedColor || '#17c1e8');
            }
        };

        if (mapInstance.isStyleLoaded()) {
            updateColors();
        } else {
            mapInstance.once('idle', updateColors);
        }
    }, [isMapLoaded, localMap, selectedColor]); 

    // 4. DATA SYNCHRONIZATION 
    useEffect(() => {
        if (!isMapLoaded || !localMap.current) return;
        const mapInstance = localMap.current;

        const uniqueZIndices = [...new Set(points.map(f => f.properties.zIndex || 1))].sort((a, b) => a - b);
        stateRef.current.uniqueZIndices = uniqueZIndices;

        const displayPoints = appMode === 'review' ? points.filter(d => selectedPointIds.includes(d.properties[idField])) : points;

        const source = mapInstance.getSource('points-source');
        if (source) source.setData({ type: 'FeatureCollection', features: displayPoints });

        uniqueZIndices.forEach((zIndex) => {
            const layerId = `points-layer-z${zIndex}`;
            if (!mapInstance.getLayer(layerId)) {
                mapInstance.addLayer({
                    id: layerId, type: 'circle', source: 'points-source', filter: ['all', ['==', ['coalesce', ['get', 'zIndex'], 1], zIndex]],
                    paint: { 'circle-color': ['get', 'feature_colour'], 'circle-radius': 9, 'circle-stroke-width': 3, 'circle-stroke-color': '#ffffff' }
                });

                addedLayersRef.current.push(layerId);

                mapInstance.on('click', layerId, (e) => {
                    e.preventDefault();
                    e.originalEvent.stopPropagation();
                    if (stateRef.current.onPointToggle) stateRef.current.onPointToggle(e.features[0].properties[idField]);
                });

                mapInstance.on('mouseenter', layerId, (e) => {
                    mapInstance.getCanvas().style.cursor = 'pointer';
                    
                    if (!e.features || e.features.length === 0) return;
                    
                    // Directly extract from the Mapbox feature instead of looking it up
                    const feature = e.features[0];
                    const coordinates = [...feature.geometry.coordinates];
                    
                    // Fallback chain prevents 'undefined'
                    const displayName = feature.properties[nameField] || feature.properties.name || feature.properties.site_name || 'Unknown';

                    const description = `<div style='text-align:center; font-size:13px'><b>${displayName}</b></div>`;

                    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) { 
                        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360; 
                    }
                    
                    if (popupRef.current) {
                        popupRef.current.setLngLat(coordinates).setHTML(description).addTo(mapInstance);
                    }
                });

                mapInstance.on('mouseleave', layerId, () => {
                    mapInstance.getCanvas().style.cursor = '';
                    if (popupRef.current) popupRef.current.remove();
                });
            }
        });
    }, [isMapLoaded, localMap, points, appMode, selectedPointIds, idField, nameField]);

    const targetBounds = useMemo(() => {
        if (!points || points.length === 0) return null;
        
        let activePoints = [];
        if (selectedPointIds.length > 0) {
            activePoints = points.filter(pt => selectedPointIds.includes(pt.properties[idField]));
        } else {
            const orgLocation = user?.organisations?.find(o => o.organisation_id === selectedOrg)?.location || { lat: 0, lng: 0 };
            const orgLng = orgLocation.lng ?? (orgLocation.longitude ?? 0);
            const orgLat = orgLocation.lat ?? (orgLocation.latitude ?? 0);
            const orgPt = turf.point([orgLng, orgLat]);

            activePoints = points.filter(pt => {
                if (!pt?.geometry?.coordinates) return false;
                const dist = turf.distance(orgPt, turf.point(pt.geometry.coordinates), { units: 'kilometers' });
                return dist < 1000; 
            });
        }

        if (activePoints.length === 0) return null;

        const bounds = new mapboxgl.LngLatBounds();
        activePoints.forEach(pt => { 
            if (pt?.geometry?.coordinates && Array.isArray(pt.geometry.coordinates)) {
                bounds.extend(pt.geometry.coordinates); 
            }
        });
        return bounds.isEmpty() ? null : bounds.toArray();
    }, [points, selectedPointIds, idField, user, selectedOrg]);

    // Initial Zoom Handler
    useEffect(() => {
        if (!isMapLoaded || !localMap.current) return;
        
        if (editGroup && editGroup.geojson && editGroup.geojson.length > 0 && groupType === (editGroup.type || 'campaigns')) {
            if (!hasFittedBounds.current) return;
        }

        if (!targetBounds) {
            if (!hasFittedBounds.current) {
                hasFittedBounds.current = true;
                const orgLocation = user?.organisations?.find(o => o.organisation_id === selectedOrg)?.location || { lat: 0, lng: 0 };
                const orgLng = orgLocation.lng ?? (orgLocation.longitude ?? 0);
                const orgLat = orgLocation.lat ?? (orgLocation.latitude ?? 0);
                
                localMap.current.stop();
                setTimeout(() => {
                    if (localMap.current) {
                        localMap.current.resize();
                        localMap.current.flyTo({ center: [orgLng, orgLat], zoom: 10, speed: 1.5 });
                    }
                }, 50);
            }
            return;
        }

        if (!hasFittedBounds.current) {
            hasFittedBounds.current = true;
            localMap.current.stop();
            setTimeout(() => {
                if (localMap.current) {
                    localMap.current.resize();
                    localMap.current.fitBounds(targetBounds, getCameraOptions());
                }
            }, 50);
        } else if (prevAppMode.current === 'review' && appMode === 'edit') {
            localMap.current.stop();
            setTimeout(() => {
                if (localMap.current) {
                    localMap.current.resize();
                    localMap.current.fitBounds(targetBounds, getCameraOptions());
                }
            }, 50);
        }
        prevAppMode.current = appMode;
    }, [isMapLoaded, localMap, targetBounds, appMode, getCameraOptions, selectedOrg, user, editGroup, groupType]);

    // Generate Trigger Syncing
    useEffect(() => {
        if (generateTrigger === 0 || !localMap.current || !isMapLoaded) return;

        const activeIds = stateRef.current.selectedPointIds;
        if (activeIds.length === 0) return;

        const selectedFeatures = stateRef.current.points.filter(d => activeIds.includes(d.properties[idField]));
        const coords = selectedFeatures.map(d => d.geometry.coordinates);

        let baseGeo;
        if (coords.length === 1) baseGeo = turf.point(coords[0]);
        else if (coords.length === 2) baseGeo = turf.lineString(coords);
        else {
            baseGeo = turf.convex(turf.points(coords));
            if (!baseGeo) baseGeo = turf.lineString(coords);
        }

        const bbox = turf.bbox(baseGeo);
        const swCorner = turf.point([bbox[0], bbox[1]]);
        const neCorner = turf.point([bbox[2], bbox[3]]);
        const diagonalDistance = turf.distance(swCorner, neCorner, { units: 'kilometers' });
        const dynamicPadding = Math.max(0.1, diagonalDistance * 0.01);

        const paddedPolygon = turf.buffer(baseGeo, (dynamicPadding > 10 ? 10 : dynamicPadding), { units: 'kilometers' });

        generatedPolygonRef.current = paddedPolygon;
        if (onPolygonGenerated) onPolygonGenerated(paddedPolygon);

        const finalSource = localMap.current.getSource('final-polygon-source');
        if (finalSource && paddedPolygon) finalSource.setData(paddedPolygon);

        if (paddedPolygon) {
            const polyBbox = turf.bbox(paddedPolygon);
            localMap.current.stop();
            setTimeout(() => {
                if (localMap.current) {
                    localMap.current.resize();
                    localMap.current.fitBounds(new mapboxgl.LngLatBounds([polyBbox[0], polyBbox[1]], [polyBbox[2], polyBbox[3]]), getCameraOptions());
                }
            }, 50);
        }

        if (draw.current) {
            draw.current.deleteAll();
            draw.current.changeMode('simple_select');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generateTrigger]);

    useEffect(() => {
        if (appMode === 'edit' && draw.current && generatedPolygonRef.current && isMapLoaded) {
            const featureIds = draw.current.add(generatedPolygonRef.current);
            draw.current.changeMode('simple_select', { featureIds });

            const finalSource = localMap.current?.getSource('final-polygon-source');
            if (finalSource) finalSource.setData({ type: 'FeatureCollection', features: [] });

            generatedPolygonRef.current = null;
        }
    }, [appMode, isMapLoaded, localMap]);

    return null;
};

export default PolygonCreator;